import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { usarSesion } from "@/lib/sesion"

export type Clase = "CONSUMIBLE" | "BIEN"

export type Unidad =
  | "UNIDAD"
  | "CAJA"
  | "PAQUETE"
  | "RESMA"
  | "LITRO"
  | "KILOGRAMO"
  | "METRO"
  | "JUEGO"

export type Articulo = {
  id: number
  codigo: string
  nombre: string
  clase: Clase
  descripcion: string | null
  unidad: Unidad
  categoria_id: number | null
  categoria: string | null
  divisa_id: number | null
  divisa: string | null
  punto_reposicion: string
  responsable: string | null
  ubicacion: string | null
  /** Si esta cosa paga IVA. Cada línea de orden que la compre lo hereda de aquí. */
  exento_iva: boolean
  activo: boolean
  existencia: string
  costo_promedio: string
  valor: string
  por_reponer: boolean
}

type CategoriaGasto = { id: number; nombre: string; activo: boolean }

export const UNIDADES: { valor: Unidad; etiqueta: string }[] = [
  { valor: "UNIDAD", etiqueta: "Unidad" },
  { valor: "CAJA", etiqueta: "Caja" },
  { valor: "PAQUETE", etiqueta: "Paquete" },
  { valor: "RESMA", etiqueta: "Resma" },
  { valor: "LITRO", etiqueta: "Litro" },
  { valor: "KILOGRAMO", etiqueta: "Kilogramo" },
  { valor: "METRO", etiqueta: "Metro" },
  { valor: "JUEGO", etiqueta: "Juego" },
]

/**
 * El alta y la edición de un artículo. **Uno solo, para los tres sitios** desde los
 * que se crea: el inventario, la ficha de la orden y el alta de la orden.
 *
 * ⚠ **Se extrajo porque había dos formularios y uno estaba a medias.** El de las
 * órdenes creaba el artículo sólo con el nombre, así que todo lo que se daba de alta
 * mientras se desglosaba una factura —que es cuándo se da de alta de verdad— nacía
 * sin categoría de gasto y sin unidad: el informe por categoría no lo agrupaba y
 * «3» no decía si eran tres resmas o tres cajas. Un formulario que pide menos según
 * por dónde se entre es un formulario que produce fichas incompletas.
 *
 * ⚠ **La clase decide qué campos se piden**, y el servidor lo vuelve a comprobar: un
 * bien no lleva punto de reposición y un consumible no lleva responsable.
 *
 * ⚠ **NO es un `<form>`, y eso no es un descuido: es el fallo que tuvo.** Desde la
 * orden esto se abre DENTRO del formulario de la línea, y un formulario anidado es
 * HTML inválido: el navegador cierra el de fuera, «Crear y usar» se comporta como el
 * envío de la página y todo lo tecleado se pierde en una recarga. Por eso los campos
 * son controlados y el botón llama a la mutación a mano, sin `onSubmit`.
 */
export function FormularioArticulo({
  articulo,
  clase,
  nombreSugerido = "",
  onCerrar,
  onGuardado,
}: {
  /** La ficha que se edita, o `null` para un alta. */
  articulo: Articulo | null
  /** En un alta, la clase con la que nace. Al editar manda la del artículo. */
  clase: Clase
  /** Lo ya tecleado desde donde se abrió: se propone como nombre. */
  nombreSugerido?: string
  onCerrar: () => void
  onGuardado: (articulo: Articulo) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const esBien = (articulo?.clase ?? clase) === "BIEN"

  // Todos controlados: sin `<form>` no hay `FormData` de donde leerlos.
  const [nombre, setNombre] = useState(articulo?.nombre ?? nombreSugerido)
  // Al editar llega el suyo: un código se teclea a medias el primer día —«ELC» en vez
  // de «ELC-03»— y sin poder corregirlo la salida era archivar el artículo y crear
  // otro, que parte su historial en dos por una errata.
  const [codigo, setCodigo] = useState(articulo?.codigo ?? "")
  const [descripcion, setDescripcion] = useState(articulo?.descripcion ?? "")
  const [unidad, setUnidad] = useState<Unidad>(articulo?.unidad ?? "UNIDAD")
  const [reposicion, setReposicion] = useState(articulo?.punto_reposicion ?? "0")
  const [responsable, setResponsable] = useState(articulo?.responsable ?? "")
  const [ubicacion, setUbicacion] = useState(articulo?.ubicacion ?? "")
  const [categoriaId, setCategoriaId] = useState<number | null>(articulo?.categoria_id ?? null)
  const [exentoIva, setExentoIva] = useState(articulo?.exento_iva ?? false)

  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<CategoriaGasto[]>("/api/v1/categorias-gasto"),
    enabled: tienePermiso(usuario, "gasto:ver"),
  })

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      articulo
        ? api.patch<Articulo>(`/api/v1/articulos/${articulo.id}`, cuerpo)
        : api.post<Articulo>("/api/v1/articulos", cuerpo),
    onSuccess: async (guardado) => {
      await clienteQuery.invalidateQueries({ queryKey: ["articulos"] })
      await clienteQuery.invalidateQueries({ queryKey: ["inventario-resumen"] })
      onGuardado(guardado)
    },
  })

  function enviar() {
    const limpio = (valor: string) => (valor.trim() === "" ? null : valor.trim())
    const cuerpo: Record<string, unknown> = {
      nombre: nombre.trim(),
      descripcion: limpio(descripcion),
      categoria_id: categoriaId,
      exento_iva: exentoIva,
      // Cada clase manda lo suyo y nada de la otra.
      ...(esBien
        ? { responsable: limpio(responsable), ubicacion: limpio(ubicacion) }
        : { unidad, punto_reposicion: decimalOpcional(limpio(reposicion)) }),
    }
    if (articulo) {
      // Vacío al editar es «déjalo como está», no «bórralo»: el artículo tiene que
      // seguir teniendo código, y el servidor rechazaría uno en blanco.
      if (limpio(codigo) !== null && codigo.trim() !== articulo.codigo) {
        cuerpo.codigo = codigo.trim()
      }
    } else {
      cuerpo.codigo = limpio(codigo)
      cuerpo.clase = clase
    }
    guardar.mutate(cuerpo)
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <div className="space-y-3 text-sm">
      <Campo
        etiqueta={esBien ? "Nombre del bien" : "Nombre del consumible"}
        ayuda="Como se reconoce en el depósito y en las órdenes."
      >
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={200}
          aria-label={esBien ? "Nombre del bien" : "Nombre del consumible"}
          className="campo"
        />
      </Campo>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* ⚠ **Se corrige también al editar, y eso no es cambiar de artículo**: lo
            que referencian las órdenes y los movimientos es su `id`, no este texto.
            Renombrar «EQP» a «EQP-01» no despega nada de su historial. */}
        <Campo
          etiqueta="Código"
          ayuda={
            articulo
              ? "Se corrige sin perder el historial: lo que referencian las órdenes es el artículo, no su código."
              : "Corto y estable: RES-CARTA. Vacío, se asigna ART-0001."
          }
        >
          <input
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            maxLength={30}
            aria-label="Código"
            className="campo"
          />
        </Campo>

        <Campo
          etiqueta="Categoría de gasto"
          ayuda="Con la que se agrupa en los informes. Sin ella, lo comprado no suma en ningún grupo."
        >
          <SelectorBuscable
            id="articulo-categoria"
            opciones={(categorias.data ?? [])
              .filter((c) => c.activo || c.id === categoriaId)
              .map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
            valor={categoriaId}
            onCambiar={setCategoriaId}
            vacio="Sin categoría"
          />
        </Campo>

        {esBien ? (
          <>
            <Campo etiqueta="Responsable" ayuda="Quién responde por él: un nombre o un área.">
              <input
                value={responsable}
                onChange={(e) => setResponsable(e.target.value)}
                maxLength={150}
                aria-label="Responsable"
                className="campo"
              />
            </Campo>
            <Campo etiqueta="Ubicación" ayuda="Dónde está: «Oficina 05».">
              <input
                value={ubicacion}
                onChange={(e) => setUbicacion(e.target.value)}
                maxLength={150}
                aria-label="Ubicación"
                className="campo"
              />
            </Campo>
          </>
        ) : (
          <>
            <Campo
              etiqueta="Unidad de medida"
              ayuda="Sin ella, «3» no dice si son tres resmas o tres cajas."
            >
              <select
                value={unidad}
                onChange={(e) => setUnidad(e.target.value as Unidad)}
                aria-label="Unidad de medida"
                className="campo"
              >
                {UNIDADES.map((u) => (
                  <option key={u.valor} value={u.valor}>
                    {u.etiqueta}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo
              etiqueta="Punto de reposición"
              ayuda="Cuando la existencia baja de aquí, avisa. Cero es no avisar."
            >
              <input
                value={reposicion}
                onChange={(e) => setReposicion(e.target.value)}
                inputMode="decimal"
                aria-label="Punto de reposición"
                className="campo tabular-nums"
              />
            </Campo>
          </>
        )}
      </div>

      <Campo etiqueta="Descripción">
        <input
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={300}
          aria-label="Descripción del artículo"
          className="campo"
        />
      </Campo>

      {/* ⚠ **Va en la ficha y no sólo en la orden**: lo exento es de la cosa comprada
          —libros, medicinas, alimentos—, no de la factura de este mes. Preguntarlo
          cada vez acaba con la misma resma gravada en una orden y exenta en la
          siguiente, y entonces el libro de compras no cuadra con ninguna de las dos. */}
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={exentoIva}
          onChange={(e) => setExentoIva(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="block font-medium">Exento de IVA</span>
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            Cada orden que lo compre lo hereda de aquí, y su renglón no suma al impuesto.
          </span>
        </span>
      </label>

      {error && <p style={{ color: "var(--destructive)" }}>{error}</p>}

      <div className="flex justify-end gap-2">
        <Boton tamano="sm" onClick={onCerrar}>
          Cancelar
        </Boton>
        {/* ⚠ **Sin `type="submit"`**, y por lo mismo que esto no es un `<form>`:
            desde la orden vive dentro del formulario de la línea, y un submit ahí
            recarga la página y se lleva por delante lo tecleado. */}
        <Boton
          tamano="sm"
          variante="primario"
          disabled={nombre.trim().length < 2 || guardar.isPending}
          onClick={enviar}
        >
          {guardar.isPending ? "Guardando…" : articulo ? "Guardar" : "Crear y usar"}
        </Boton>
      </div>
    </div>
  )
}

function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string
  ayuda?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <span className="block font-medium">{etiqueta}</span>
      {children}
      {ayuda && (
        <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
          {ayuda}
        </span>
      )}
    </div>
  )
}
